export interface UploadProgressInfo {
  loaded: number;
  total: number;
  speed: number; // bytes per second
}

export class UploadProgressTracker {
  private startTime: number = 0;
  private lastProgressTime: number = 0;
  private lastProgressLoaded: number = 0;
  private estimatedSpeed: number = 0;
  private speedHistory: number[] = [];
  private progressCallback?: (progress: UploadProgressInfo) => void;
  private intervalId?: number;
  private randomSeed: number = Math.random();

  constructor(
    private total: number,
    onProgress?: (progress: UploadProgressInfo) => void
  ) {
    this.progressCallback = onProgress;
  }

  start(): void {
    this.startTime = Date.now();
    this.lastProgressTime = this.startTime;
    this.startProgressSimulation();
  }

  updateProgress(loaded: number): void {
    const now = Date.now();
    const timeDiff = now - this.lastProgressTime;

    if (timeDiff > 0) {
      const loadedDiff = loaded - this.lastProgressLoaded;
      const currentSpeed = (loadedDiff / timeDiff) * 1000; // bytes per second

      // Mantieni una storia delle velocità per una media più stabile
      this.speedHistory.push(currentSpeed);
      if (this.speedHistory.length > 10) {
        this.speedHistory.shift();
      }

      // Calcola la velocità media
      this.estimatedSpeed =
        this.speedHistory.reduce((sum, speed) => sum + speed, 0) /
        this.speedHistory.length;
    }

    this.lastProgressTime = now;
    this.lastProgressLoaded = loaded;

    this.progressCallback?.({
      loaded,
      total: this.total,
      speed: this.estimatedSpeed
    });
  }

  getCurrentSpeed(): number {
    return this.estimatedSpeed;
  }

  private startProgressSimulation(): void {
    this.intervalId = window.setInterval(() => {
      if (this.estimatedSpeed > 0) {
        const now = Date.now();
        const timeSinceLastUpdate = now - this.lastProgressTime;

        // Aggiungi randomizzazione alla velocità (±15%)
        const randomFactor = 0.85 + this.generateRandomNoise() * 0.3;
        const adjustedSpeed = this.estimatedSpeed * randomFactor;

        // Simula progress basato sulla velocità stimata con randomizzazione
        const estimatedIncrement = (adjustedSpeed * timeSinceLastUpdate) / 1000;
        const estimatedLoaded = Math.min(
          this.lastProgressLoaded + estimatedIncrement,
          this.total
        );

        // Emetti solo se c'è un incremento significativo
        if (estimatedLoaded > this.lastProgressLoaded + 1024) {
          // 1KB threshold
          this.progressCallback?.({
            loaded: Math.floor(estimatedLoaded),
            total: this.total,
            speed: adjustedSpeed
          });
        }
      }
    }, 100); // Aggiorna ogni 100ms
  }

  private generateRandomNoise(): number {
    // Genera rumore pseudo-casuale basato sul tempo e seed
    const time = Date.now();
    const noise =
      Math.sin(time * 0.001 + this.randomSeed) *
      Math.cos(time * 0.0007 + this.randomSeed * 2) *
      Math.sin(time * 0.0013 + this.randomSeed * 3);
    return (noise + 1) / 2; // Normalizza tra 0 e 1
  }

  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  complete(): void {
    this.stop();
    this.progressCallback?.({
      loaded: this.total,
      total: this.total,
      speed: this.estimatedSpeed
    });
  }
}
